import { PaginationBlock } from '@finn/design-system/atoms/pagination';
import { Tabs, TabsList, TabsTrigger } from '@finn/design-system/atoms/tabs';
import { cn, parseToHtml, trackEvent } from '@finn/ui-utils';
import dayjs from 'dayjs';
import Link from 'next/link';
import { redirect, usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';

import { ArticleCard } from './ArticleCard';
import { InternalLinksMetadata } from './meta';

type Props = {
  data: InternalLinksMetadata;
};

const mobileGridRowsClasses = {
  1: 'grid-rows-1',
  2: 'grid-rows-2',
  3: 'grid-rows-3',
  4: 'grid-rows-4',
  5: 'grid-rows-5',
  6: 'grid-rows-6',
  7: 'grid-rows-7',
  8: 'grid-rows-8',
  9: 'grid-rows-9',
};

const borderClasses = 'border-pearl border data-[state=active]:border-black';
const colorClasses =
  'hover:bg-snow data-[state=active]:hover:bg-snow data-[state=active]:bg-white';

const N_ROWS_GRID = 8;
const N_COLS_GRID = 3;

const N_ARTICLES_PER_PAGE = N_ROWS_GRID * N_COLS_GRID;

export const InternalLinks: React.FC<Props> = ({ data }) => {
  const [page, setPage] = useState(0);
  const pathName = usePathname();

  const selectedTag = useMemo(() => {
    const tagsFromUrl = pathName.split('auto/');

    return tagsFromUrl.length > 1 ? tagsFromUrl[1] : 'all-tags';
  }, [pathName]);

  const allArticles = useMemo(() => {
    const shownSlugs = {};
    const shownPages = [];

    Object.values(data.pagesByTags)
      .flat()
      .forEach((item) => {
        if (!shownSlugs[item.slug]) {
          shownSlugs[item.slug] = true;
          shownPages.push(item);
        }
      });

    return shownPages.sort(
      (a, b) => dayjs(b.publishedAt).valueOf() - dayjs(a.publishedAt).valueOf()
    );
  }, [data.pagesByTags]);

  const articlesBySelectedTag =
    selectedTag === 'all-tags' ? allArticles : data?.pagesByTags?.[selectedTag];

  if (!data.tags.find((tag) => tag.slug === selectedTag)) {
    redirect('/404');
  }

  const articlesToShow = useMemo(() => {
    return articlesBySelectedTag?.slice(
      page * N_ARTICLES_PER_PAGE,
      (page + 1) * N_ARTICLES_PER_PAGE
    );
  }, [articlesBySelectedTag, page]);

  useEffect(() => {
    // when opening a new tag we go back to first page of articles
    setPage(0);
  }, [pathName]);

  return (
    <div className="container">
      <h1 className="mb-8 mobile-t1-semibold md:web-t1-semibold">
        {data.title}
      </h1>
      <Tabs
        defaultValue={selectedTag}
        value={selectedTag}
        className="mt-2 w-full"
      >
        <TabsList>
          {data.tags?.map((tag) => (
            <TabsTrigger
              asChild
              key={tag.slug}
              value={tag.slug}
              className={cn(
                borderClasses,
                colorClasses,
                '!h-10 !min-h-10 !px-3 !body-14-regular'
              )}
              onClick={() => {
                trackEvent('Tag Clicked', {
                  tagSlug: tag.slug,
                  tagTitle: tag.title,
                });
              }}
            >
              <Link
                href={tag.slug === 'all-tags' ? '/auto' : `/auto/${tag.slug}`}
              >
                {parseToHtml(tag.title)}
              </Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div
        className={cn(
          'mt-6 grid w-full grid-cols-[minmax(343px,1fr)] gap-x-6 gap-y-10 sm:grid-cols-[repeat(2,minmax(343px,1fr))] md:grid-cols-[repeat(3,minmax(343px,1fr))]',
          mobileGridRowsClasses[articlesToShow.length],
          {
            'md:grid-rows-1':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 1,
            'md:grid-rows-2':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 2,
            'md:grid-rows-3':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 3,
            'md:grid-rows-4':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 4,
            'md:grid-rows-5':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 5,
            'md:grid-rows-6':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 6,
            'md:grid-rows-7':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 7,
            'md:grid-rows-8':
              Math.ceil(articlesToShow.length / N_ROWS_GRID) === 8,
          }
        )}
      >
        {articlesToShow?.map((pageInfo) => (
          <ArticleCard key={pageInfo.slug} article={pageInfo} />
        ))}
      </div>
      <div className="mt-10 w-full">
        <PaginationBlock
          page={page + 1}
          totalPages={Math.ceil(
            articlesBySelectedTag.length / N_ARTICLES_PER_PAGE
          )}
          className="justify-center"
          onPageChange={(pageNumber) => {
            setPage(pageNumber - 1);
          }}
        />
      </div>
    </div>
  );
};
